// import { ClickEventHandler } from '../modules/dom/_ClickEvent';
import { StyleController } from '../modules/dom/_CssProperty';

/**
 * // Class Obj: モーダルウィンドウ全体の入口処理を管理
 * // Note:
 * // - イベント登録はこのクラスに集約します。
 * // - 開閉状態、表示切替、コンテンツ描画などの実処理は各クラスへ委譲します。
 * // - ModalState 側ではイベント登録を行わず、状態遷移だけを担当します。
 */
export class ModalDisplay
{
  private static readonly CONTENT_NAVI_SHOW_DELAY_TIME = 2000;
  private static readonly CLASS_CONTENT_NAVI_VISIBLE = 'is_content_navi_visible';

  private mContentNaviShowTimerId: number | null = null;

  private readonly mModalPortal: ModalPortal;
  private readonly mModalAttachment: ModalAttachment;
  private readonly mModalControl: ModalControl;
  private readonly mModalSwipeHandler: ModalSwipeHandler;
  private readonly mModalWheelHandler: ModalWheelHandler;
  private readonly mModalContentCollection: ModalContentCollection;
  private readonly mModalContentState: ModalContentState;
  private readonly mModalContentNavigator: ModalContentNavigator;
  private readonly mModalContentRenderer: ModalContentRenderer;
  private readonly mModalState: ModalState;
  private readonly mMediaPlaybackController: MediaPlaybackController;

  /**
   * // Method: モーダル制御に必要な各クラスを初期化する
   * // Note:
   * // - target_modals を基準に、操作ボタン・モーダル本体・表示用コンテンツを収集します。
   */
  constructor(site_body: HTMLElement, target_modals: HTMLElement | null)
  {
    this.mModalPortal = new ModalPortal();
    this.mModalAttachment = new ModalAttachment(site_body);
    this.mModalControl = new ModalControl(target_modals, this.mModalPortal);
    this.mModalSwipeHandler = new ModalSwipeHandler();
    this.mModalWheelHandler = new ModalWheelHandler();
    this.mMediaPlaybackController = new MediaPlaybackController();
    this.mModalContentCollection = new ModalContentCollection(target_modals);
    this.mModalContentState = new ModalContentState();
    this.mModalContentNavigator = new ModalContentNavigator(
      this.mModalContentCollection,
      this.mModalContentState,
    );
    this.mModalContentRenderer = new ModalContentRenderer();
    this.mModalState = new ModalState(this.mModalAttachment, this.mModalControl, this.mModalPortal);

  }

  /**
   * // Method: モーダル関連のイベント登録を実行する
   * // Note:
   * // - open / close / window切替 / item選択 / contentナビゲーションを登録します。
   */
  public runProgram(): void
  {
    this.bindOpen(this.mModalControl.mOpenButtons);
    this.bindClose(this.mModalControl.mCloseButtons);
    this.bindOverlayClose(this.mModalAttachment.mOverlay);

    this.bindSwitch(this.mModalControl.mPrevButtons);
    this.bindSwitch(this.mModalControl.mNextButtons);

    this.bindSelectItem(this.mModalControl.mChooseListButtons);
    this.bindContentNavigation(this.mModalControl.mContentNavButtons);

    this.bindSwipeNavigation(this.mModalControl.getAllModals());
    this.bindWheelNavigation(this.mModalControl.getAllModals());

    this.bindContentNavPointerInteraction(this.mModalControl.getAllModals());
  }

  /**
 * // Method: モーダル内を触ったときにナビゲーションボタンの表示タイマーを再起動する
 * // Note:
 * // - タップ / クリック / ペン操作を pointerdown でまとめて扱います。
 * // - 動画再生中はタイマーを再起動せず、非表示のままにします。
 */
  private bindContentNavPointerInteraction(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      modal.addEventListener('pointerdown', () =>
      {
        if (!this.mModalControl.isActiveModal(modal))
        {
          return;
        }

        if (this.mMediaPlaybackController.hasPlayingVideosInElement(modal))
        {
          this.clearContentNavVisibilityTimer();
          this.hideContentNavButtons(modal);

          return;
        }

        this.restartContentNavVisibilityTimer(modal);
      });
    });
  }

  /**
   * // Method: 複数要素へ click イベントを登録する共通処理
   */
  private bindClickEvent(
    target_elements: HTMLElement[],
    callback: (element: HTMLElement) => void,
  ): void
  {
    target_elements.forEach((element: HTMLElement) =>
    {
      element.addEventListener('click', () =>
      {
        callback(element);
      });
    });
  }

  /**
   * // Method: モーダルを開くボタンのイベントを登録する
   * // Note:
   * // - モーダル本体を開いた後、汎用コンテンツを反映します。
   */
  private bindOpen(open_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      open_buttons,
      (open_button: HTMLElement) =>
      {
        this.mModalState.openByElement(open_button);

        this.renderContentByElement(open_button);
      },
    );
  }

  /**
   * // Method: モーダルを閉じるボタンのイベントを登録する
   * // Note:
   * // - close は個別閉じではなく、全モーダルを閉じる処理へ統一しています。
   */
  private bindClose(close_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      close_buttons,
      (_element: HTMLElement) =>
      {
        this.closeAll();
      },
    );
  }

  /**
   * // Method: オーバーレイクリック時の閉じるイベントを登録する
   */
  private bindOverlayClose(target_element: HTMLElement | null): void
  {
    if (!target_element)
    {
      return;
    }

    target_element.addEventListener('click', () =>
    {
      this.closeAll();
    });
  }

  /**
   * // Method: モーダルウィンドウ自体の前後切替イベントを登録する
   * // Note:
   * // - data-modal-prev / data-modal-next は、表示するモーダル本体の切替に使います。
   * // - 汎用コンテンツの next-item 等とは役割を分けています。
   */
  private bindSwitch(switch_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      switch_buttons,
      (element: HTMLElement) =>
      {
        this.mModalControl.changeWindowByElement(element);
      },
    );
  }

  /**
   * // Method: モーダル内のスワイプによるコンテンツ移動イベントを登録する
   * // Note:
   * // - モーダルが is_active のときだけ反応します。
   * // - 左右スワイプは item 移動、上下スワイプは group 移動として扱います。
   */
  private bindSwipeNavigation(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.mModalSwipeHandler.bind(
        modal,
        (swipe_direction: ModalSwipeDirection) =>
        {
          if (!this.mModalControl.isActiveModal(modal))
          {
            return;
          }

          const navigation_type: ModalContentNavigationType =
            this.getContentNavigationTypeBySwipeDirection(swipe_direction);

          this.renderContentByNavigationType(navigation_type, modal);
        },
        () =>
        {
          return this.mModalControl.isActiveModal(modal);
        },
      );
    });
  }

  /**
 * // Method: 汎用コンテンツ用ナビゲーションボタンの遅延表示を開始する
 * // Note:
 * // - 画像切り替え直後はいったん非表示にします。
 * // - 一定時間後、移動可能なボタンだけふわっと表示します。
 */
  private restartContentNavVisibilityTimer(target_modal: HTMLElement | null): void
  {
    this.clearContentNavVisibilityTimer();
    this.hideContentNavButtons(target_modal);

    this.mContentNaviShowTimerId = window.setTimeout(() =>
    {
      this.showContentNavButtons(target_modal);
      this.mContentNaviShowTimerId = null;
    }, ModalDisplay.CONTENT_NAVI_SHOW_DELAY_TIME);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを一時的に非表示にする
   */
  private hideContentNavButtons(target_modal: HTMLElement | null): void
  {
    if (!target_modal)
    {
      return;
    }

    target_modal.classList.remove(ModalDisplay.CLASS_CONTENT_NAVI_VISIBLE);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを表示可能状態にする
   */
  private showContentNavButtons(target_modal: HTMLElement | null): void
  {
    if (!target_modal)
    {
      return;
    }

    if (!this.mModalControl.isActiveModal(target_modal))
    {
      return;
    }

    target_modal.classList.add(ModalDisplay.CLASS_CONTENT_NAVI_VISIBLE);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンの遅延表示タイマーを解除する
   */
  private clearContentNavVisibilityTimer(): void
  {
    if (this.mContentNaviShowTimerId === null)
    {
      return;
    }

    window.clearTimeout(this.mContentNaviShowTimerId);
    this.mContentNaviShowTimerId = null;
  }

  /**
   * // Method: スワイプ方向を汎用コンテンツ用ナビゲーション種別へ変換する
   */
  private getContentNavigationTypeBySwipeDirection(
    swipe_direction: ModalSwipeDirection,
  ): ModalContentNavigationType
  {
    if (swipe_direction === 'left')
    {
      return 'next-item';
    }

    if (swipe_direction === 'right')
    {
      return 'prev-item';
    }

    if (swipe_direction === 'up')
    {
      return 'next-group';
    }

    return 'prev-group';
  }

  /**
   * // Method: モーダル内の任意アイテム選択イベントを登録する
   * // Note:
   * // - サムネイル一覧や選択リストから、表示内容を差し替える用途を想定しています。
   */
  private bindSelectItem(select_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      select_buttons,
      (select_button: HTMLElement) =>
      {
        this.renderContentByElement(select_button);
      },
    );
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションのイベントを登録する
   * // Note:
   * // - next-item / prev-item / next-group / prev-group は、必要な場合のみHTML側に配置します。
   */
  private bindContentNavigation(nav_buttons: HTMLElement[]): void
  {
    this.bindClickEvent(
      nav_buttons,
      (element: HTMLElement) =>
      {
        this.renderContentByNavigation(element);
      },
    );
  }

  /**
   * // Method: モーダル内のホイール操作による group 移動イベントを登録する
   * // Note:
   * // - PC の上下スクロール操作のみを対象にします。
   * // - 下方向は next-group、上方向は prev-group として扱います。
   */
  private bindWheelNavigation(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.mModalWheelHandler.bind(
        modal,
        (wheel_direction: ModalWheelDirection) =>
        {
          if (!this.mModalControl.isActiveModal(modal))
          {
            return;
          }

          const navigation_type: ModalContentNavigationType =
            this.getContentNavigationTypeByWheelDirection(wheel_direction);

          this.renderContentByNavigationType(navigation_type, modal);
        },
      );
    });
  }

  /**
   * // Method: ホイール方向を汎用コンテンツ用ナビゲーション種別へ変換する
   */
  private getContentNavigationTypeByWheelDirection(
    wheel_direction: ModalWheelDirection,
  ): ModalContentNavigationType
  {
    if (wheel_direction === 'down')
    {
      return 'next-group';
    }

    return 'prev-group';
  }

  /**
   * // Method: クリック元に紐づく汎用コンテンツをモーダルへ描画する
   */
  private renderContentByElement(element: HTMLElement): void
  {
    const content_item: ModalContentItem | null =
      this.mModalContentCollection.getItemByElement(element);

    if (!content_item)
    {
      return;
    }

    const target_modal: HTMLElement | null = this.mModalControl.getRelatedModalByElement(element);

    this.mModalContentState.setCurrentItem(content_item);

    if (target_modal)
    {
      this.mMediaPlaybackController.stopVideosInElement(target_modal);
    }

    const content_display: HTMLElement | null = this.mModalContentRenderer.render(
      content_item,
      target_modal,
    );

    this.updateContentNavButtonVisibility(target_modal);
    this.startContentNavVisibilityByRenderedContent(target_modal, content_display);

    if (content_display)
    {
      this.mMediaPlaybackController.playVideosInElement(content_display);
    }
  }

  /**
   * // Method: ナビゲーション種別に応じて次に表示する汎用コンテンツを描画する
   */
  private renderContentByNavigation(element: HTMLElement): void
  {
    const navigation_type: ModalContentNavigationType | null =
      this.mModalControl.getContentNavigationTypeByElement(element);

    if (!navigation_type)
    {
      return;
    }

    const target_modal: HTMLElement | null = this.mModalControl.getRelatedModalByElement(element);

    this.renderContentByNavigationType(navigation_type, target_modal);
  }

  /**
   * // Method: 指定ナビゲーション種別に応じて次に表示する汎用コンテンツを描画する
   */
  private renderContentByNavigationType(
    navigation_type: ModalContentNavigationType,
    target_modal: HTMLElement | null,
  ): void
  {
    const content_item: ModalContentItem | null =
      this.getContentItemByNavigationType(navigation_type);

    if (!content_item)
    {
      return;
    }

    this.mModalContentState.setCurrentItem(content_item);

    if (target_modal)
    {
      this.mMediaPlaybackController.stopVideosInElement(target_modal);
    }

    const content_display: HTMLElement | null = this.mModalContentRenderer.render(
      content_item,
      target_modal,
    );

    this.updateContentNavButtonVisibility(target_modal);
    this.startContentNavVisibilityByRenderedContent(target_modal, content_display);

    if (content_display)
    {
      this.mMediaPlaybackController.playVideosInElement(content_display);
    }
  }

  /**
   * // Method: ナビゲーション種別から移動先のコンテンツを取得する
   */
  private getContentItemByNavigationType(
    navigation_type: ModalContentNavigationType,
  ): ModalContentItem | null
  {
    if (navigation_type === 'next-item')
    {
      return this.mModalContentNavigator.getNextItem();
    }

    if (navigation_type === 'prev-item')
    {
      return this.mModalContentNavigator.getPrevItem();
    }

    if (navigation_type === 'next-group')
    {
      return this.mModalContentNavigator.getNextGroup();
    }

    if (navigation_type === 'prev-group')
    {
      return this.mModalContentNavigator.getPrevGroup();
    }

    return null;
  }

  /**
 * // Method: 現在表示中の content 状態に応じてナビゲーションボタンの表示状態を更新する
 */
  private updateContentNavButtonVisibility(target_modal: HTMLElement | null): void
  {
    const nav_buttons: HTMLElement[] =
      this.mModalControl.getContentNavButtonsByModal(target_modal);

    nav_buttons.forEach((nav_button: HTMLElement) =>
    {
      const navigation_type: ModalContentNavigationType | null =
        this.mModalControl.getContentNavigationTypeByElement(nav_button);

      if (!navigation_type)
      {
        nav_button.hidden = true;
        return;
      }

      const content_item: ModalContentItem | null =
        this.getContentItemByNavigationType(navigation_type);

      const is_visible: boolean = content_item !== null;

      nav_button.hidden = !is_visible;
      nav_button.toggleAttribute('aria-hidden', !is_visible);

      if (nav_button instanceof HTMLButtonElement)
      {
        nav_button.disabled = !is_visible;
      }
    });
  }

  /**
   * // Method: モーダルをすべて閉じ、汎用コンテンツの状態も初期化する
   */
  private closeAll(): void
  {
    this.clearContentNavVisibilityTimer();

    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.hideContentNavButtons(modal);
      this.mMediaPlaybackController.stopVideosInElement(modal);
    });

    this.mModalState.closeAll();
    this.mModalContentState.reset();
  }

  /**
 * // Method: 描画されたコンテンツに応じてナビゲーションボタンの表示制御を開始する
 * // Note:
 * // - 画像など video がない場合は、一定時間後に表示します。
 * // - video がある場合は、再生終了後に表示します。
 */
  private startContentNavVisibilityByRenderedContent(
    target_modal: HTMLElement | null,
    content_display: HTMLElement | null,
  ): void
  {
    this.clearContentNavVisibilityTimer();
    this.hideContentNavButtons(target_modal);

    if (!target_modal)
    {
      return;
    }

    if (!content_display)
    {
      this.restartContentNavVisibilityTimer(target_modal);
      return;
    }

    if (!this.mMediaPlaybackController.hasVideosInElement(content_display))
    {
      this.restartContentNavVisibilityTimer(target_modal);
      return;
    }

    this.mMediaPlaybackController.bindVideosEndedInElement(
      content_display,
      () =>
      {
        if (!this.mModalControl.isActiveModal(target_modal))
        {
          return;
        }

        this.showContentNavButtons(target_modal);
      },
    );
  }
}

type ModalSwipeDirection = 'left' | 'right' | 'up' | 'down';

/**
 * // Class Obj: モーダル内のスワイプ操作を判定する
 * // Note:
 * // - touchstart / touchend を監視し、一定距離以上のスワイプのみ反応します。
 * // - 横スワイプ / 縦スワイプを判定し、方向だけを ModalDisplay 側へ返します。
 * // - 実際のモーダル移動処理は担当しません。
 */
class ModalSwipeHandler
{
  private static readonly MIN_DISTANCE = 50;
  private static readonly DIRECTION_RATIO = 1.25;

  private mStartX: number | null = null;
  private mStartY: number | null = null;

  public bind(
    target_element: HTMLElement,
    callback: (swipe_direction: ModalSwipeDirection) => void,
    should_prevent_default: () => boolean = () => true,
  ): void
  {
    target_element.addEventListener(
      'touchstart',
      (touch_event: TouchEvent) =>
      {
        this.handleTouchStart(touch_event);
      },
      { passive: true },
    );

    target_element.addEventListener(
      'touchmove',
      (touch_event: TouchEvent) =>
      {
        this.handleTouchMove(touch_event, should_prevent_default);
      },
      { passive: false },
    );

    target_element.addEventListener(
      'touchend',
      (touch_event: TouchEvent) =>
      {
        this.handleTouchEnd(touch_event, callback);
      },
      { passive: true },
    );

    target_element.addEventListener(
      'touchcancel',
      () =>
      {
        this.resetTouchPosition();
      },
      { passive: true },
    );
  }

  private handleTouchStart(touch_event: TouchEvent): void
  {
    const touch: Touch | undefined = touch_event.touches[0];

    if (!touch)
    {
      return;
    }

    this.mStartX = touch.clientX;
    this.mStartY = touch.clientY;
  }

  private handleTouchMove(
    touch_event: TouchEvent,
    should_prevent_default: () => boolean,
  ): void
  {
    if (!should_prevent_default())
    {
      return;
    }

    if (this.mStartX === null || this.mStartY === null)
    {
      return;
    }

    touch_event.preventDefault();
  }

  private handleTouchEnd(
    touch_event: TouchEvent,
    callback: (swipe_direction: ModalSwipeDirection) => void,
  ): void
  {
    if (this.mStartX === null || this.mStartY === null)
    {
      return;
    }

    const touch: Touch | undefined = touch_event.changedTouches[0];

    if (!touch)
    {
      this.resetTouchPosition();
      return;
    }

    const diff_x: number = touch.clientX - this.mStartX;
    const diff_y: number = touch.clientY - this.mStartY;

    this.resetTouchPosition();

    const swipe_direction: ModalSwipeDirection | null = this.getSwipeDirection(diff_x, diff_y);

    if (!swipe_direction)
    {
      return;
    }

    callback(swipe_direction);
  }

  private getSwipeDirection(diff_x: number, diff_y: number): ModalSwipeDirection | null
  {
    const abs_x: number = Math.abs(diff_x);
    const abs_y: number = Math.abs(diff_y);

    if (abs_x < ModalSwipeHandler.MIN_DISTANCE && abs_y < ModalSwipeHandler.MIN_DISTANCE)
    {
      return null;
    }

    if (abs_x > abs_y * ModalSwipeHandler.DIRECTION_RATIO)
    {
      return diff_x < 0 ? 'left' : 'right';
    }

    if (abs_y > abs_x * ModalSwipeHandler.DIRECTION_RATIO)
    {
      return diff_y < 0 ? 'up' : 'down';
    }

    return null;
  }

  private resetTouchPosition(): void
  {
    this.mStartX = null;
    this.mStartY = null;
  }
}

type ModalWheelDirection = 'up' | 'down';

/**
 * // Class Obj: モーダル内のホイール操作を判定する
 * // Note:
 * // - PC の mouse wheel / trackpad scroll を監視します。
 * // - 上下スクロールのみを group 移動として扱います。
 * // - 連続発火しすぎないように、一定時間内の再実行を抑制します。
 */
class ModalWheelHandler
{
  private static readonly MIN_DELTA_Y = 40;
  private static readonly INTERVAL_MS = 450;

  private mLastExecutedTime: number = 0;

  /**
   * // Method: 指定要素へ wheel イベントを登録する
   */
  public bind(
    target_element: HTMLElement,
    callback: (wheel_direction: ModalWheelDirection) => void,
  ): void
  {
    target_element.addEventListener(
      'wheel',
      (wheel_event: WheelEvent) =>
      {
        this.handleWheel(wheel_event, callback);
      },
      { passive: false },
    );
  }

  /**
   * // Method: ホイール方向を判定する
   */
  private handleWheel(
    wheel_event: WheelEvent,
    callback: (wheel_direction: ModalWheelDirection) => void,
  ): void
  {
    const abs_delta_y: number = Math.abs(wheel_event.deltaY);

    if (abs_delta_y < ModalWheelHandler.MIN_DELTA_Y)
    {
      return;
    }

    const current_time: number = Date.now();

    if (current_time - this.mLastExecutedTime < ModalWheelHandler.INTERVAL_MS)
    {
      wheel_event.preventDefault();
      return;
    }

    this.mLastExecutedTime = current_time;

    wheel_event.preventDefault();

    callback(wheel_event.deltaY > 0 ? 'down' : 'up');
  }
}

/**
 * // Class Obj: モーダルウィンドウの操作対象となる要素と表示切り替えを管理
 * // Note:
 * // - data-modal-* 属性を基準に、開閉ボタンや切替ボタンを収集します。
 * // - ここでは「どのモーダルを表示するか」の制御に集中します。
 * // - 汎用コンテンツの中身そのものは ModalContent 系のクラスで扱います。
 */
class ModalControl
{
  private static readonly SELECTOR_MODAL = '.modal';
  private static readonly SELECTOR_OPEN_BUTTON = '[data-modal-open]';
  private static readonly SELECTOR_CLOSE_BUTTON = '[data-modal-close]';
  private static readonly SELECTOR_PREV_BUTTON = '[data-modal-prev]';
  private static readonly SELECTOR_NEXT_BUTTON = '[data-modal-next]';
  private static readonly SELECTOR_PAGER_BUTTON = '[data-modal-pager]';
  private static readonly SELECTOR_CHOOSE_LIST_BUTTON = '[data-modal-choose-item]';
  private static readonly SELECTOR_CONTENT_NAV_BUTTON = '[data-modal-content-navi]';

  private static readonly ATTR_TARGET = 'data-modal-target';
  private static readonly ATTR_CONTENT_NAV = 'data-modal-content-navi';
  private static readonly CLASS_ACTIVE = 'is_active';

  private readonly mStyleController: StyleController;
  private readonly mModalPortal: ModalPortal;
  private readonly mFallbackRoot: HTMLElement;

  public readonly mModalRoot: HTMLElement | null;

  private readonly mAllModals: HTMLElement[];

  public readonly mOpenButtons: HTMLElement[];
  public readonly mCloseButtons: HTMLElement[];
  public readonly mNextButtons: HTMLElement[];
  public readonly mPrevButtons: HTMLElement[];
  public readonly mPagerButtons: HTMLElement[];
  public readonly mChooseListButtons: HTMLElement[];
  public readonly mContentNavButtons: HTMLElement[];

  /**
   * // Method: モーダル操作に使う要素を収集する
   * // Note:
   * // - modal_root が null の場合は空の root を使い、意図しない document 全体検索を避けます。
   */
  constructor(modal_root: HTMLElement | null, modal_portal: ModalPortal)
  {
    this.mStyleController = new StyleController();
    this.mModalPortal = modal_portal;
    this.mFallbackRoot = document.createElement('div');

    this.mModalRoot = modal_root;

    this.mOpenButtons = this.getElementsBySelector(ModalControl.SELECTOR_OPEN_BUTTON);
    this.mCloseButtons = this.getElementsBySelector(ModalControl.SELECTOR_CLOSE_BUTTON);
    this.mPrevButtons = this.getElementsBySelector(ModalControl.SELECTOR_PREV_BUTTON);
    this.mNextButtons = this.getElementsBySelector(ModalControl.SELECTOR_NEXT_BUTTON);
    this.mPagerButtons = this.getElementsBySelector(ModalControl.SELECTOR_PAGER_BUTTON);
    this.mChooseListButtons = this.getElementsBySelector(ModalControl.SELECTOR_CHOOSE_LIST_BUTTON);
    this.mContentNavButtons = this.getElementsBySelector(ModalControl.SELECTOR_CONTENT_NAV_BUTTON);

    this.mAllModals = this.getElementsBySelector(ModalControl.SELECTOR_MODAL);
  }

  /**
   * // Method: 指定したモーダルが表示中か判定する
   */
  public isActiveModal(modal: HTMLElement): boolean
  {
    return modal.classList.contains(ModalControl.CLASS_ACTIVE);
  }

  /**
   * // Method: root 配下から指定セレクタに一致する要素を配列で取得する
   */
  private getElementsBySelector(selector: string): HTMLElement[]
  {
    const root: HTMLElement = this.mModalRoot ?? this.mFallbackRoot;

    return Array.from(root.querySelectorAll<HTMLElement>(selector));
  }

  /**
   * // Method: data-modal-target に紐づくモーダル要素を取得する
   * // Note:
   * // - 値に # が含まれていても取得できるように補正します。
   */
  public getTargetModalByElement(element: HTMLElement): HTMLElement | null
  {
    const target_modal_id: string | null = element.getAttribute(ModalControl.ATTR_TARGET);

    if (!target_modal_id)
    {
      return null;
    }

    return document.getElementById(target_modal_id.replace(/^#/, ''));
  }

  /**
   * // Method: 要素に関連するモーダルを取得する
   * // Note:
   * // - モーダル内のボタンなら closest('.modal') を優先します。
   * // - 開くボタンなどモーダル外の要素なら data-modal-target から取得します。
   */
  public getRelatedModalByElement(element: HTMLElement): HTMLElement | null
  {
    const current_modal: HTMLElement | null = element.closest<HTMLElement>(ModalControl.SELECTOR_MODAL);

    return current_modal ?? this.getTargetModalByElement(element);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーション種別を取得する
   */
  public getContentNavigationTypeByElement(element: HTMLElement): ModalContentNavigationType | null
  {
    const navigation_type: string | null = element.getAttribute(ModalControl.ATTR_CONTENT_NAV);

    if (
      navigation_type === 'next-item' ||
      navigation_type === 'prev-item' ||
      navigation_type === 'next-group' ||
      navigation_type === 'prev-group'
    )
    {
      return navigation_type;
    }

    return null;
  }

  /**
   * // Method: 指定したモーダルを表示する
   */
  private showModal(modal: HTMLElement): void
  {
    modal.classList.add(ModalControl.CLASS_ACTIVE);
  }

  /**
   * // Method: 指定したモーダルを非表示にする
   */
  private hideModal(modal: HTMLElement): void
  {
    modal.classList.remove(ModalControl.CLASS_ACTIVE);
  }

  /**
   * // Method: 管理対象の全モーダルを取得する
   */
  public getAllModals(): HTMLElement[]
  {
    return this.mAllModals;
  }

  /**
   * // Method: 管理対象の全モーダルを非表示にする
   */
  public hideAllModals(): void
  {
    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      this.hideModal(modal);
    });
  }

  /**
   * // Method: data-modal-target に応じて表示するモーダルを切り替える
   * // Note:
   * // - 現在のモーダルを非表示にし、対象モーダルを表示します。
   * // - 対象モーダルは表示前に body 直下へ portal 化します。
   */
  public changeWindowByElement(element: HTMLElement): void
  {
    const current_modal: HTMLElement | null = element.closest<HTMLElement>(ModalControl.SELECTOR_MODAL);
    const target_modal: HTMLElement | null = this.getTargetModalByElement(element);

    if (target_modal)
    {
      this.mModalPortal.portal(target_modal);
    }

    if (current_modal)
    {
      this.hideModal(current_modal);
    }

    if (target_modal)
    {
      this.showModal(target_modal);
    }

    this.mAllModals.forEach((modal: HTMLElement) =>
    {
      if (modal !== current_modal && modal !== target_modal)
      {
        this.hideModal(modal);
      }
    });
  }

  /**
 * // Method: 指定モーダル内の汎用コンテンツ用ナビゲーションボタンを取得する
 */
  public getContentNavButtonsByModal(modal: HTMLElement | null): HTMLElement[]
  {
    if (!modal)
    {
      return [];
    }

    return Array.from(
      modal.querySelectorAll<HTMLElement>(ModalControl.SELECTOR_CONTENT_NAV_BUTTON),
    );
  }
}

type ModalContentNavigationType = 'next-item' | 'prev-item' | 'next-group' | 'prev-group';

type ModalContentItem = {
  group_name: string;
  item_index: number;
  element: HTMLElement;
  content: HTMLElement;
};

/**
 * // Class Obj: モーダルに表示する汎用コンテンツ群を管理
 * // Note:
 * // - data-modal-content-item を起点に、表示対象の content を収集します。
 * // - group / index を保持することで、next-item や next-group の移動計算に使える形にします。
 * // - 画像・動画に限定せず、口コミ、商品詳細、任意HTMLなどを扱えます。
 */
class ModalContentCollection
{
  private static readonly DEFAULT_GROUP_NAME = 'default';

  private static readonly SELECTOR_CONTENT_ITEM = '[data-modal-content-item]:not([data-modal-content-disabled])';
  private static readonly SELECTOR_CONTENT_SOURCE = '[data-modal-content-source]';

  private static readonly ATTR_CONTENT_GROUP = 'data-modal-content-group';
  private static readonly ATTR_CONTENT_INDEX = 'data-modal-content-index';
  private static readonly ATTR_CONTENT_TARGET = 'data-modal-content-target';

  private readonly mRoot: HTMLElement;
  private readonly mContentItemMap: WeakMap<HTMLElement, ModalContentItem> = new WeakMap();
  private readonly mGroupItemsMap: Map<string, ModalContentItem[]> = new Map();

  /**
   * // Method: 汎用コンテンツの収集対象 root を設定し、初期収集を実行する
   */
  constructor(root: HTMLElement | null)
  {
    this.mRoot = root ?? document.body;

    this.setupItems();
  }

  /**
   * // Method: content item と content の対応関係を作成する
   * // Note:
   * // - data-modal-content-target があれば、指定IDの要素を content として扱います。
   * // - 指定がなければ content item 内の data-modal-content-source を content として扱います。
   */
  private setupItems(): void
  {
    const content_item_elements: HTMLElement[] = Array.from(
      this.mRoot.querySelectorAll<HTMLElement>(ModalContentCollection.SELECTOR_CONTENT_ITEM),
    );

    const group_count_map: Map<string, number> = new Map();

    content_item_elements.forEach((content_item_element: HTMLElement) =>
    {
      const content: HTMLElement | null = this.getContentSourceByItem(content_item_element);

      if (!content)
      {
        return;
      }

      const group_name: string = this.getGroupNameByItem(content_item_element);
      const item_index: number = this.getItemIndexByItem(content_item_element, group_name, group_count_map);

      const content_item: ModalContentItem = {
        group_name,
        item_index,
        element: content_item_element,
        content,
      };

      this.mContentItemMap.set(content_item_element, content_item);

      const group_items: ModalContentItem[] = this.mGroupItemsMap.get(group_name) ?? [];
      group_items.push(content_item);

      this.mGroupItemsMap.set(group_name, group_items);
    });

    this.sortItems();
  }

  /**
   * // Method: グループ内の item を index 順に並び替える
   */
  private sortItems(): void
  {
    this.mGroupItemsMap.forEach((items: ModalContentItem[]) =>
    {
      items.sort((first_item: ModalContentItem, second_item: ModalContentItem) =>
      {
        return first_item.item_index - second_item.item_index;
      });
    });
  }

  /**
   * // Method: content item から group 名を取得する
   */
  private getGroupNameByItem(content_item_element: HTMLElement): string
  {
    return content_item_element.getAttribute(ModalContentCollection.ATTR_CONTENT_GROUP)
      ?? ModalContentCollection.DEFAULT_GROUP_NAME;
  }

  /**
   * // Method: content item から item index を取得する
   * // Note:
   * // - data-modal-content-index があればその値を優先します。
   * // - 指定がない場合は、同一 group 内の登場順を index として扱います。
   */
  private getItemIndexByItem(
    content_item_element: HTMLElement,
    group_name: string,
    group_count_map: Map<string, number>,
  ): number
  {
    const index_value: string | null = content_item_element.getAttribute(ModalContentCollection.ATTR_CONTENT_INDEX);

    if (index_value !== null)
    {
      const parsed_index: number = Number(index_value);

      if (!Number.isNaN(parsed_index))
      {
        return parsed_index;
      }
    }

    const current_count: number = group_count_map.get(group_name) ?? 0;
    group_count_map.set(group_name, current_count + 1);

    return current_count;
  }

  /**
   * // Method: content item に対応する表示元 content を取得する
   */
  private getContentSourceByItem(content_item_element: HTMLElement): HTMLElement | null
  {
    const target_id: string | null = content_item_element.getAttribute(ModalContentCollection.ATTR_CONTENT_TARGET);

    if (target_id)
    {
      return document.getElementById(target_id.replace(/^#/, ''));
    }

    return content_item_element.querySelector<HTMLElement>(
      `:scope > ${ModalContentCollection.SELECTOR_CONTENT_SOURCE}`,
    ) ?? content_item_element.querySelector<HTMLElement>(
      ModalContentCollection.SELECTOR_CONTENT_SOURCE,
    );
  }

  /**
   * // Method: content item 要素に対応する ModalContentItem を取得する
   */
  public getItemByContentItem(content_item_element: HTMLElement): ModalContentItem | null
  {
    return this.mContentItemMap.get(content_item_element) ?? null;
  }

  /**
   * // Method: 任意の要素から最も近い content item を探し、対応する ModalContentItem を取得する
   * // Note:
   * // - button[data-modal-open] から呼ばれても、親方向の data-modal-content-item を参照できます。
   * // - data-modal-content-item 自身から呼ばれた場合も、そのまま取得できます。
   */
  public getItemByElement(element: HTMLElement): ModalContentItem | null
  {
    const content_item_element: HTMLElement | null = element.closest<HTMLElement>(
      ModalContentCollection.SELECTOR_CONTENT_ITEM,
    );

    if (!content_item_element)
    {
      return null;
    }

    return this.getItemByContentItem(content_item_element);
  }

  /**
   * // Method: 指定 group に属する item 一覧を取得する
   */
  public getItemsByGroup(group_name: string): ModalContentItem[]
  {
    return this.mGroupItemsMap.get(group_name) ?? [];
  }

  /**
   * // Method: 登録済みの group 名一覧を取得する
   */
  public getGroupNames(): string[]
  {
    return Array.from(this.mGroupItemsMap.keys());
  }

  /**
   * // Method: group 名と index から item を取得する
   */
  public getItemByPosition(group_name: string, item_index: number): ModalContentItem | null
  {
    const items: ModalContentItem[] = this.getItemsByGroup(group_name);

    return items.find((item: ModalContentItem) =>
    {
      return item.item_index === item_index;
    }) ?? null;
  }
}

/**
 * // Class Obj: モーダルに現在表示している汎用コンテンツの状態を管理
 * // Note:
 * // - 現在の group / item index をたどれるように、表示中の ModalContentItem を保持します。
 * // - next-item / prev-item / next-group / prev-group の基準点になります。
 */
class ModalContentState
{
  private mCurrentItem: ModalContentItem | null = null;

  /**
   * // Method: 現在表示中の item を更新する
   */
  public setCurrentItem(content_item: ModalContentItem): void
  {
    this.mCurrentItem = content_item;
  }

  /**
   * // Method: 現在表示中の item を取得する
   */
  public getCurrentItem(): ModalContentItem | null
  {
    return this.mCurrentItem;
  }

  /**
   * // Method: 現在表示中の group 名を取得する
   */
  public getCurrentGroupName(): string | null
  {
    return this.mCurrentItem?.group_name ?? null;
  }

  /**
   * // Method: 現在表示中の item index を取得する
   */
  public getCurrentItemIndex(): number | null
  {
    return this.mCurrentItem?.item_index ?? null;
  }

  /**
   * // Method: 現在表示中の item 状態を初期化する
   */
  public reset(): void
  {
    this.mCurrentItem = null;
  }
}

/**
 * // Class Obj: モーダルに表示する汎用コンテンツの移動先を管理
 * // Note:
 * // - このクラスは「次にどの item を表示するか」を計算するだけです。
 * // - 実際の描画は ModalContentRenderer、現在状態の更新は ModalContentState が担当します。
 */
class ModalContentNavigator
{
  private readonly mContentCollection: ModalContentCollection;
  private readonly mContentState: ModalContentState;

  constructor(content_collection: ModalContentCollection, content_state: ModalContentState)
  {
    this.mContentCollection = content_collection;
    this.mContentState = content_state;
  }

  /**
   * // Method: 同一 group 内の次の item を取得する
   */
  public getNextItem(): ModalContentItem | null
  {
    return this.getMovedItem(1);
  }

  /**
   * // Method: 同一 group 内の前の item を取得する
   */
  public getPrevItem(): ModalContentItem | null
  {
    return this.getMovedItem(-1);
  }

  /**
   * // Method: 次の group の item を取得する
   */
  public getNextGroup(): ModalContentItem | null
  {
    return this.getMovedGroupItem(1);
  }

  /**
   * // Method: 前の group の item を取得する
   */
  public getPrevGroup(): ModalContentItem | null
  {
    return this.getMovedGroupItem(-1);
  }

  /**
   * // Method: 同一 group 内で offset 分移動した item を取得する
   */
  private getMovedItem(offset: number): ModalContentItem | null
  {
    const current_item: ModalContentItem | null = this.mContentState.getCurrentItem();

    if (!current_item)
    {
      return null;
    }

    const group_items: ModalContentItem[] = this.mContentCollection.getItemsByGroup(
      current_item.group_name,
    );

    const current_position: number = group_items.findIndex((item: ModalContentItem) =>
    {
      return item.item_index === current_item.item_index;
    });

    if (current_position < 0)
    {
      return null;
    }

    const moved_position: number = current_position + offset;

    if (moved_position < 0 || moved_position >= group_items.length)
    {
      return null;
    }

    return group_items[moved_position] ?? null;
  }

  /**
   * // Method: group 単位で offset 分移動した item を取得する
   * // Note:
   * // - group を移動した場合は、移動先 group の先頭 item を返します。
   * // - item_index は引き継がず、group 移動時に 0 番目相当へ戻します。
   */
  private getMovedGroupItem(offset: number): ModalContentItem | null
  {
    const current_item: ModalContentItem | null = this.mContentState.getCurrentItem();

    if (!current_item)
    {
      return null;
    }

    const group_names: string[] = this.mContentCollection.getGroupNames();

    const current_group_position: number = group_names.findIndex((group_name: string) =>
    {
      return group_name === current_item.group_name;
    });

    if (current_group_position < 0)
    {
      return null;
    }

    const moved_group_position: number = current_group_position + offset;

    if (moved_group_position < 0 || moved_group_position >= group_names.length)
    {
      return null;
    }

    const moved_group_name: string | undefined = group_names[moved_group_position];

    if (!moved_group_name)
    {
      return null;
    }

    const moved_group_items: ModalContentItem[] = this.mContentCollection.getItemsByGroup(
      moved_group_name,
    );

    return moved_group_items[0] ?? null;
  }
}

/**
 * // Class Obj: モーダル内へ汎用コンテンツを描画
 * // Note:
 * // - data-modal-content-display を描画先として扱います。
 * // - 表示元 content は clone して描画するため、元DOMは壊しません。
 * // - template 要素にも対応し、任意HTMLの差し替えに使えるようにしています。
 */
class ModalContentRenderer
{
  private static readonly SELECTOR_CONTENT_DISPLAY = '[data-modal-content-display]';

  /**
   * // Method: 指定 item の content を対象モーダル内へ描画する
   */
  public render(content_item: ModalContentItem, modal: HTMLElement | null): HTMLElement | null
  {
    const content_display: HTMLElement | null = this.getContentDisplay(modal);

    if (!content_display)
    {
      return null;
    }

    const cloned_content: Node = this.cloneContent(content_item.content);

    content_display.replaceChildren(cloned_content);

    return content_display;
  }

  /**
   * // Method: 汎用コンテンツの描画先を取得する
   */
  private getContentDisplay(modal: HTMLElement | null): HTMLElement | null
  {
    if (!modal)
    {
      return null;
    }

    return modal.querySelector<HTMLElement>(ModalContentRenderer.SELECTOR_CONTENT_DISPLAY);
  }

  /**
   * // Method: 表示元 content を描画用に複製する
   * // Note:
   * // - hidden / aria-hidden が付いている元要素でも、モーダル内では表示できるように属性を外します。
   */
  private cloneContent(content: HTMLElement): Node
  {
    if (content instanceof HTMLTemplateElement)
    {
      return content.content.cloneNode(true);
    }

    const cloned_content: HTMLElement = content.cloneNode(true) as HTMLElement;
    cloned_content.removeAttribute('hidden');
    cloned_content.removeAttribute('aria-hidden');

    return cloned_content;
  }
}

/**
 * // Class Obj: モーダル内メディアの再生状態を管理
 * // Note:
 * // - 描画済みの要素内にある video を自動再生します。
 * // - ブラウザの自動再生制限に配慮し、muted / playsInline / autoplay を補完します。
 * // - モーダルを閉じる前に video を停止できるようにします。
 */
class MediaPlaybackController
{
  /**
   * // Method: 指定要素内の video を自動再生する
   */
  public playVideosInElement(element: HTMLElement): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.playVideo(video);
    });
  }

  /**
   * // Method: 指定要素内の video を停止する
   */
  public stopVideosInElement(element: HTMLElement): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    videos.forEach((video: HTMLVideoElement) =>
    {
      video.pause();
      video.currentTime = 0;
    });
  }

  /**
   * // Method: 指定要素内の video 一覧を取得する
   */
  private getVideosInElement(element: HTMLElement): HTMLVideoElement[]
  {
    if (element instanceof HTMLVideoElement)
    {
      return [element];
    }

    return Array.from(element.querySelectorAll<HTMLVideoElement>('video'));
  }

  /**
   * // Method: video の自動再生を試みる
   */
  private playVideo(video: HTMLVideoElement): void
  {
    video.controls = true;
    video.setAttribute('controlslist', 'nodownload');
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;

    const play_promise: Promise<void> | undefined = video.play();

    if (!play_promise)
    {
      return;
    }

    play_promise.catch(() =>
    {
      // ブラウザ側で自動再生が拒否された場合は何もしない
    });
  }

  /**
 * // Method: 指定要素内に video があるか判定する
 */
  public hasVideosInElement(element: HTMLElement): boolean
  {
    return this.getVideosInElement(element).length > 0;
  }

  /**
   * // Method: 指定要素内に再生中の video があるか判定する
   */
  public hasPlayingVideosInElement(element: HTMLElement): boolean
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    return videos.some((video: HTMLVideoElement) =>
    {
      return !video.paused && !video.ended;
    });
  }

  /**
   * // Method: 指定要素内の video 再生終了時に処理を実行する
   */
  public bindVideosEndedInElement(
    element: HTMLElement,
    callback: () => void,
  ): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    if (videos.length === 0)
    {
      callback();
      return;
    }

    let remaining_count: number = videos.filter((video: HTMLVideoElement) =>
    {
      return !video.ended;
    }).length;

    if (remaining_count === 0)
    {
      callback();
      return;
    }

    videos.forEach((video: HTMLVideoElement) =>
    {
      if (video.ended)
      {
        return;
      }

      video.addEventListener(
        'ended',
        () =>
        {
          remaining_count -= 1;

          if (remaining_count <= 0)
          {
            callback();
          }
        },
        { once: true },
      );
    });
  }
}

/**
 * // Class Obj: モーダルウィンドウに付属するパーツ（オーバーレイ等）を管理
 * // Note:
 * // - オーバーレイの生成・表示切替を担当します。
 * // - body のスクロール制御もここに集約します。
 */
class ModalAttachment
{
  private readonly mStyleController: StyleController;
  private readonly mSiteBody: HTMLElement;
  public readonly mOverlay: HTMLElement;

  constructor(site_body: HTMLElement)
  {
    this.mStyleController = new StyleController();
    this.mSiteBody = site_body;

    this.mOverlay = this.ensureOverlay();
  }

  /**
   * // Method: オーバーレイ要素を取得、なければ生成する
   */
  private ensureOverlay(): HTMLElement
  {
    let overlay = document.getElementById('modalOverlay') as HTMLElement | null;

    if (!overlay)
    {
      overlay = document.createElement('div');
      overlay.id = 'modalOverlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.display = 'none';

      document.body.appendChild(overlay);
    }

    return overlay;
  }

  /**
   * // Method: オーバーレイの表示状態を切り替える
   */
  public toggleOverlay(show: boolean): void
  {
    this.mStyleController.applyDisplayToElement(this.mOverlay.style, show ? 'block' : 'none');
    this.mOverlay.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  /**
   * // Method: body のスクロール可否を切り替える
   */
  public toggleScrollBody(enable: boolean): void
  {
    this.mSiteBody.classList.toggle('no_scroll', !enable);
  }
}

/**
 * // Class Obj: モーダルウィンドウの状態（表示、非表示）を管理
 * // Note:
 * // - 開く / 閉じる状態の変更だけを担当します。
 * // - イベント登録や表示内容の描画は行いません。
 */
class ModalState
{
  private readonly mModalAttachment: ModalAttachment;
  private readonly mModalControl: ModalControl;
  private readonly mModalPortal: ModalPortal;

  private mIsWindowOpen: boolean = false;
  private mLastOpenElement: HTMLElement | null = null;

  constructor(modal_attachment: ModalAttachment, modal_control: ModalControl, modal_portal: ModalPortal)
  {
    this.mModalAttachment = modal_attachment;
    this.mModalControl = modal_control;
    this.mModalPortal = modal_portal;
  }

  /**
   * // Method: 指定要素に紐づくモーダルを開く
   * // Note:
   * // - 開く対象は data-modal-target から取得します。
   * // - 開く前に対象モーダルを body 直下へ portal 化します。
   */
  public openByElement(element: HTMLElement): void
  {
    const target_modal: HTMLElement | null = this.mModalControl.getTargetModalByElement(element);

    if (!target_modal)
    {
      return;
    }

    this.mLastOpenElement = element;

    this.mModalPortal.portal(target_modal);
    this.mModalControl.changeWindowByElement(element);

    this.mModalAttachment.toggleOverlay(true);
    this.mModalAttachment.toggleScrollBody(false);

    this.mIsWindowOpen = true;
  }

  /**
   * // Method: 開いているモーダルをすべて閉じる
   * // Note:
   * // - overlay / bodyスクロール / portal 復帰をまとめて処理します。
   * // - 最後に開いた要素へ focus を戻します。
   */
  public closeAll(): void
  {
    if (!this.mIsWindowOpen)
    {
      return;
    }

    this.mModalControl.hideAllModals();

    this.mModalAttachment.toggleOverlay(false);
    this.mModalAttachment.toggleScrollBody(true);

    this.mModalPortal.restoreAll(this.mModalControl.getAllModals());

    this.mIsWindowOpen = false;

    this.mLastOpenElement?.focus();
    this.mLastOpenElement = null;
  }
}

/**
 * // Class Obj: モーダルの portal 化（DOM退避・復帰）を管理
 * // Note:
 * // - モーダルを body 直下へ移動し、z-index や親要素の overflow の影響を受けにくくします。
 * // - 元の位置にはコメントノードを差し込み、閉じるときに元のDOM位置へ復帰します。
 */
class ModalPortal
{
  private readonly mPlaceholderMap: WeakMap<HTMLElement, Comment> = new WeakMap();
  private readonly mParentMap: WeakMap<HTMLElement, Node> = new WeakMap();
  /**
   * // Method: 指定したモーダルが body 直下へ退避済みか判定する
   */
  public isPortaled(modal: HTMLElement): boolean
  {
    return this.mPlaceholderMap.has(modal);
  }

  /**
   * // Method: モーダルを body 直下へ移動する
   * // Note:
   * // - 元位置には復帰用のコメントノードを残します。
   * // - 二重 portal を避けるため、退避済みの場合は何もしません。
   */
  public portal(modal: HTMLElement): void
  {
    if (this.isPortaled(modal))
    {
      return;
    }

    const parent = modal.parentNode;
    if (!parent)
    {
      return;
    }

    const placeholder = document.createComment('modal-origin');
    parent.insertBefore(placeholder, modal);

    this.mParentMap.set(modal, parent);
    this.mPlaceholderMap.set(modal, placeholder);

    document.body.appendChild(modal);
  }

  /**
   * // Method: body 直下へ退避したモーダルを元のDOM位置へ戻す
   */
  public restore(modal: HTMLElement): void
  {
    const placeholder = this.mPlaceholderMap.get(modal);
    const parent = this.mParentMap.get(modal);

    if (!placeholder || !parent)
    {
      return;
    }

    placeholder.parentNode?.insertBefore(modal, placeholder);
    placeholder.remove();

    this.mPlaceholderMap.delete(modal);
    this.mParentMap.delete(modal);
  }

  /**
   * // Method: 複数のモーダルをまとめて元のDOM位置へ戻す
   */
  public restoreAll(modals: HTMLElement[]): void
  {
    modals.forEach((modal: HTMLElement) =>
    {
      this.restore(modal);
    });
  }
}
